import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Shield, AlertTriangle, UserX, Flag, MessageSquare } from "lucide-react";
import Footer from "@/components/Footer";

const EULAPage = () => {
  const navigate = useNavigate();
  const lastUpdated = "February 17, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50" data-testid="eula-page">
      {/* Header - Transparent Style */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/logo-main.png" 
              alt="RecoveryMatch" 
              className="w-10 h-10 rounded-xl"
            />
            <span className="font-bold text-xl">
              <span className="text-[#1C3A7A]">Recovery</span>
              <span className="text-[#FAB710]">Match</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-[#1C3A7A] transition-colors">Features</button>
            <button onClick={() => navigate('/founder')} className="hover:text-[#1C3A7A] transition-colors">About</button>
            <button onClick={() => navigate('/faq')} className="hover:text-[#1C3A7A] transition-colors">FAQ</button>
            <button onClick={() => navigate('/contact')} className="hover:text-[#1C3A7A] transition-colors">Contact</button>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/login')}
              className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition px-4 py-2 hidden sm:block"
            >
              Log In
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="bg-[#FAB710] text-white px-5 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-200 hover:scale-105 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 pt-28 pb-12">
        {/* Page Title */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-[#1C3A7A]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1C3A7A] mb-2">
            End User License Agreement
          </h1>
          <p className="text-gray-500">Last updated: {lastUpdated}</p>
        </div>

        {/* Zero Tolerance Banner */}
        <div className="bg-blue-50 border-l-4 border-[#1C3A7A] rounded-lg p-6 mb-8 flex items-start gap-4">
          <Shield className="w-8 h-8 text-[#1C3A7A] flex-shrink-0 mt-1" />
          <div>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-2">Zero Tolerance Policy</h2>
            <p className="text-gray-700">
              RecoveryMatch has <strong>ZERO TOLERANCE</strong> for objectionable content or abusive users. 
              Violations will result in immediate content removal and account termination.
            </p>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {/* Agreement */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              This End User License Agreement ("EULA") is a legal agreement between you and RecoveryMatch.com, LLC 
              ("RecoveryMatch", "we", "us", or "our") for the use of the RecoveryMatch mobile application and related services.
            </p>
            <p className="text-gray-600 leading-relaxed">
              By downloading, installing, or using RecoveryMatch, you agree to be bound by the terms of this EULA. 
              If you do not agree to these terms, do not use the application.
            </p>
          </section>

          {/* Content Standards */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              2. User Content Standards
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              RecoveryMatch is committed to maintaining a safe, supportive environment for individuals in recovery. 
              You agree that you will <strong>NOT</strong> post, upload, share, or transmit any content that:
            </p>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">×</span>
                Is sexually explicit, pornographic, or obscene
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">×</span>
                Promotes violence, self-harm, or substance abuse
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">×</span>
                Contains hate speech or discriminatory language
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">×</span>
                Harasses, bullies, intimidates, or threatens others
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">×</span>
                Is defamatory, libelous, or fraudulent
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">×</span>
                Infringes on intellectual property rights
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">×</span>
                Encourages or glorifies substance use or relapse
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold">×</span>
                Promotes illegal activities
              </li>
            </ul>
          </section>

          {/* Zero Tolerance Details */}
          <section className="bg-red-50 rounded-2xl border border-red-200 p-6">
            <h2 className="text-xl font-bold text-red-700 mb-4">3. Zero Tolerance Policy Details</h2>
            <div className="bg-white rounded-lg p-4 border border-red-200 mb-4">
              <p className="text-red-700 font-semibold">
                Any content that violates our standards will be removed immediately, and the user responsible 
                may be permanently banned from RecoveryMatch without warning or refund.
              </p>
            </div>
            <p className="text-gray-700">
              Our moderation team reviews reported content within 24 hours. Users who repeatedly violate 
              these standards will face immediate account termination.
            </p>
          </section>

          {/* Reporting & Blocking */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-4 flex items-center gap-2">
              <Flag className="w-5 h-5 text-[#FAB710]" />
              4. Reporting & Blocking
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              RecoveryMatch provides robust tools to help you maintain a safe experience:
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-xl p-4">
                <Flag className="w-6 h-6 text-[#1C3A7A] mb-2" />
                <h3 className="font-semibold text-[#1C3A7A] mb-1">Report Content</h3>
                <p className="text-sm text-gray-600">
                  Use the report button on any profile, message, or content that violates our standards.
                </p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4">
                <UserX className="w-6 h-6 text-[#1C3A7A] mb-2" />
                <h3 className="font-semibold text-[#1C3A7A] mb-1">Block Users</h3>
                <p className="text-sm text-gray-600">
                  Block any user. They won't be able to view your profile or contact you.
                </p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4">
                <MessageSquare className="w-6 h-6 text-[#1C3A7A] mb-2" />
                <h3 className="font-semibold text-[#1C3A7A] mb-1">24-Hour Response</h3>
                <p className="text-sm text-gray-600">
                  All reports are reviewed within 24 hours by our moderation team.
                </p>
              </div>
            </div>
          </section>

          {/* Content Moderation */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-4">5. Content Moderation</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              RecoveryMatch employs a combination of automated and human moderation:
            </p>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                Automated content filtering for known objectionable patterns
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                Human review of all reported content within 24 hours
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                Proactive monitoring of public content
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                Immediate removal of content that violates our standards
              </li>
            </ul>
            <div className="mt-4 p-4 bg-green-50 rounded-lg">
              <p className="text-green-800 font-medium">
                Our developer (RecoveryMatch.com, LLC) commits to acting on objectionable content reports 
                within 24 hours by removing the content and ejecting the user who provided it.
              </p>
            </div>
          </section>

          {/* Enforcement */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-4">6. Enforcement Actions</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Violations of this EULA may result in one or more of the following actions:
            </p>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-yellow-500">⚠</span>
                Warning notification
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500">⚠</span>
                Content removal
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500">⚠</span>
                Temporary account suspension
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-700 font-bold">⛔</span>
                Permanent account termination
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-700 font-bold">⛔</span>
                Report to law enforcement (for illegal activity)
              </li>
            </ul>
          </section>

          {/* Contact */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-4">7. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed mb-2">
              To report objectionable content or user behavior:
            </p>
            <ul className="text-gray-600 mb-4">
              <li>• Use the in-app <strong>Report</strong> button</li>
              <li>• Email: <a href="mailto:support@recoverymatch.com" className="text-[#1C3A7A] hover:underline">support@recoverymatch.com</a></li>
            </ul>
            <p className="text-gray-600">
              For general EULA inquiries: <a href="mailto:support@recoverymatch.com" className="text-[#1C3A7A] hover:underline">support@recoverymatch.com</a>
            </p>
          </section>
        </div>

        {/* Back Button */}
        <div className="mt-10 text-center">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-[#1C3A7A] hover:text-[#FAB710] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EULAPage;
